import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Paper,
  Button,
  ButtonGroup,
  makeStyles,
  withStyles,
} from "@material-ui/core";
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Check as CheckIcon,
} from "@material-ui/icons";
import { useSelector, useDispatch } from "react-redux";
import removalActions from "../../../redux/actions/externalRemovals";
import formatDateToUTC from "../../../helpers/formatDateUtc";

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "greenPoint",
    numeric: false,
    disablePadding: false,
    label: "Punto GIRA",
  },
  {
    id: "kilograms",
    numeric: false,
    disablePadding: false,
    label: "Cantidad de material",
  },
  {
    id: "createdAt",
    numeric: false,
    disablePadding: false,
    label: "Fecha de registro",
  },
  {
    id: "datetimeRemoval",
    numeric: false,
    disablePadding: false,
    label: "Fecha de validación",
  },
  {
    id: "requester",
    numeric: false,
    disablePadding: false,
    label: "Registrado por",
  },
  {
    id: "approver",
    numeric: false,
    disablePadding: false,
    label: "Validado por",
  },
  { id: "options", numeric: false, disablePadding: false, label: "Opciones" },
];

function EnhancedTableHead(props) {
  const { classes, order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "default"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              <strong>{headCell.label}</strong>
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
}));

export default function EnhancedTable() {
  const removals = useSelector((state) => state.externalRemovals);
  const auth = useSelector((state) => state.auth);

  const dispatch = useDispatch();

  const classes = useStyles();
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("datetimeRemoval");
  const [dense] = useState(false);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (removals.removals) {
      setRows(removals.removals);
    }
  }, [removals.removals]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (_event, page) => {
    if (page > removals.pagination.page) {
      if (rows.length === removals.pagination.rows) {
        dispatch(removalActions.setPagination({ page }));
      }
    } else {
      dispatch(removalActions.setPagination({ page }));
    }
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch(
      removalActions.setPagination({
        rows: parseInt(event.target.value, 10),
      })
    );
  };

  const emptyRows = removals.pagination.rows - rows.length;

  const options = ({ action, data }) => {
    if (action === "VIEW") {
      dispatch(removalActions.setDataModal(data));
      dispatch(removalActions.setViewModal(true));
    }
    if (action === "DELETE") {
      dispatch(removalActions.setDataModal(data));
      dispatch(removalActions.setDeleteModal(true));
    }
    if (action === "EDIT") {
      dispatch(removalActions.setDataModal(data));
      dispatch(removalActions.setEditModal(true));
    }
    if (action === "APROBE") {
      dispatch(removalActions.setDataModal(data));
      dispatch(removalActions.setAprobeModal(true));
    }
  };

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <TableContainer>
          <Table
            className={classes.table}
            size="small"
            aria-labelledby="tableTitle"
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {stableSort(rows, getComparator(order, orderBy)).map(
                (row, index) => (
                  <TableRow hover tabIndex={-1} key={index}>
                    <StyledTableCell align="left">
                      {row.greenPoint}
                    </StyledTableCell>
                    <StyledTableCell align="left">
                      {Math.round(row.kilograms * 100) / 100 + " Kg"}
                    </StyledTableCell>
                    <StyledTableCell align="left">
                      {formatDateToUTC(row.createdAt)}
                    </StyledTableCell>

                    <StyledTableCell align="left">
                      {formatDateToUTC(row.datetimeRemoval)}
                    </StyledTableCell>

                    <StyledTableCell align="left">
                      {row.requester
                        ? `${row.requester.name ?? ""} ${row.requester.lastName ?? ""}`
                        : ""}
                    </StyledTableCell>
                    <StyledTableCell align="left">
                      {row.approver
                        ? `${row.approver.name ?? ""} ${row.approver.lastName ?? ""}`
                        : ""}
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      {removals.selector === "COMPLETES" ? (
                        <ButtonGroup size="small" fullWidth variant="contained">
                          <Button
                            onClick={() =>
                              options({ action: "VIEW", data: row })
                            }
                            color="primary"
                          >
                            <VisibilityIcon />
                          </Button>
                          <Button
                            onClick={() =>
                              options({ action: "EDIT", data: row })
                            }
                            color="primary"
                          >
                            <EditIcon />
                          </Button>
                          <Button
                            onClick={() =>
                              options({ action: "DELETE", data: row })
                            }
                            color="secondary"
                          >
                            <DeleteIcon />
                          </Button>
                        </ButtonGroup>
                      ) : (
                        <ButtonGroup size="small" fullWidth variant="contained">
                          <Button
                            onClick={() =>
                              options({ action: "APROBE", data: row })
                            }
                            color="primary"
                          >
                            <CheckIcon />
                          </Button>
                          <Button
                            disabled={row._id === auth.user._id}
                            onClick={() =>
                              options({ action: "DELETE", data: row })
                            }
                            color="secondary"
                          >
                            <DeleteIcon />
                          </Button>
                        </ButtonGroup>
                      )}
                    </StyledTableCell>
                  </TableRow>
                )
              )}
              {emptyRows > 0 && (
                <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          labelRowsPerPage={"Filas por página"}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to === -1 ? count : to} de ${
              count !== -1 ? count : `más de ${to}`
            }`
          }
          rowsPerPageOptions={[7, 14, 28]}
          component="div"
          count={-1}
          rowsPerPage={removals.pagination.rows}
          page={removals.pagination.page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
}
